// src/pages/Sitemap.jsx - NEVXOAULIO VENTURES SITEMAP (Updated)
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Map, 
  Home, 
  Briefcase, 
  FileText, 
  Users, 
  HelpCircle, 
  Folder,
  MessageSquare,
  Shield,
  TrendingUp,
  UserCog,
  Eye,
  MessageCircle
} from 'lucide-react';

const Sitemap = () => {
  const sitemapSections = [
    {
      title: 'Main Pages',
      icon: Home,
      links: [
        { label: 'Home', path: '/' },
        { label: 'About Us', path: '/about' },
        { label: 'Contact', path: '/contact' },
        { label: 'Portfolio', path: '/portfolio' },
        { label: 'FAQ', path: '/faq' },
      ],
    },
    {
      title: 'Services',
      icon: Briefcase,
      links: [
        { label: 'All Services Overview', path: '/services' },
        { label: 'Software Development', path: '/services/software-development' },
        { label: 'Cloud Computing', path: '/services/cloud-computing' },
        { label: 'Cyber Security', path: '/services/cyber-security' },
        { label: 'VLSI Design', path: '/services/vlsi-design' },
        { label: 'AI & ML Solutions', path: '/services/ai-ml-solutions' },
        { label: 'Data Analytics', path: '/services/data-analytics' },
        { label: 'VLSI Services', path: '/services/vlsi' },
        { label: 'Blockchain & Web3', path: '/services/blockchain' },
        { label: 'Game Development', path: '/services/game-development' },
        { label: 'AI Automation', path: '/services/ai-automation' },
        { label: 'Startup MVP', path: '/services/startup-mvp' },
      ],
    },
    {
      title: 'Community & Engagement',
      icon: Users,
      links: [
        { label: 'Forum & Chat', path: '/forum' },
        { label: 'Customer Feedback', path: '/feedback' },
        { label: 'Single Feedback View', path: '/feedback/:id' },
        { label: 'Blog', path: '/blog' },
        { label: 'Individual Blog Post', path: '/blog/:id' },
        { label: 'Careers', path: '/careers' },
      ],
    },
    {
      title: 'User Account',
      icon: UserCog,
      links: [
        { label: 'Client Sign In', path: '/login' },
        { label: 'Client Sign Up', path: '/signup' },
        { label: 'Client Dashboard', path: '/client/dashboard' },
      ],
    },
    {
      title: 'Administration',
      icon: Shield,
      links: [
        { label: 'Admin Login', path: '/admin/login' },
        { label: 'Admin Dashboard', path: '/admin/dashboard' },
        { label: 'Analytics Dashboard', path: '/admin/analytics' },
        { label: 'Manage News & Blog', path: '/admin/manage-news' },
        { label: 'Manage Feedback', path: '/admin/manage-feedback' },
        { label: 'Admin Chat Support', path: '/admin/chat' },
      ],
    },
    {
      title: 'Legal & Information',
      icon: FileText,
      links: [
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Sitemap (Current Page)', path: '/sitemap' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-white transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Header */}
        <div className="mb-8 sm:mb-12 text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
            <Map className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 dark:text-blue-400" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 sm:mt-0">
              Sitemap
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-sm sm:text-base px-4">
            Welcome to the complete sitemap of Nevxoaulio Ventures. Here you'll find all the pages and services available on our platform. Navigate through our comprehensive offerings and explore everything we have to offer.
          </p>
        </div>

        {/* Mobile-Friendly Navigation */}
        <div className="lg:hidden mb-8">
          <div className="bg-blue-50 dark:bg-gray-800 p-4 rounded-lg mb-6">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <HelpCircle className="w-5 h-5" />
              Quick Navigation
            </h3>
            <select 
              className="w-full p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
              onChange={(e) => {
                if (e.target.value) {
                  window.location.href = e.target.value;
                }
              }}
            >
              <option value="">Jump to a page...</option>
              {sitemapSections.flatMap(section => 
                section.links.map(link => (
                  <option key={link.path} value={link.path}>
                    {link.label}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        {/* Sitemap Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16">
          {sitemapSections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 p-4 sm:p-6 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-400 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                </div>

                <ul className="space-y-2 sm:space-y-3">
                  {section.links.map((link, idx) => (
                    <li key={idx}>
                      <Link
                        to={link.path}
                        className="text-sm sm:text-base text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200 flex items-center gap-2 group"
                      >
                        <span className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"></span>
                        <span className="group-hover:translate-x-1 transition-transform truncate">
                          {link.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Quick Stats - Responsive Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="bg-blue-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg text-center border-l-2 sm:border-l-4 border-blue-600">
            <p className="text-2xl sm:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1 sm:mb-2">8</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">Main Pages</p>
          </div>

          <div className="bg-green-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg text-center border-l-2 sm:border-l-4 border-green-600">
            <p className="text-2xl sm:text-3xl font-bold text-green-600 dark:text-green-400 mb-1 sm:mb-2">12</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">Services</p>
          </div>

          <div className="bg-purple-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg text-center border-l-2 sm:border-l-4 border-purple-600">
            <p className="text-2xl sm:text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1 sm:mb-2">6</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">Community Areas</p>
          </div>

          <div className="bg-orange-50 dark:bg-gray-800 p-4 sm:p-6 rounded-lg text-center border-l-2 sm:border-l-4 border-orange-600">
            <p className="text-2xl sm:text-3xl font-bold text-orange-600 dark:text-orange-400 mb-1 sm:mb-2">40+</p>
            <p className="text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">Total Pages</p>
          </div>
        </div>

        {/* Services Overview - Responsive */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-6 sm:p-8 lg:p-10 rounded-lg mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
            <Briefcase className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
            Our Service Categories
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">Technology Solutions</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Software Development
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Cloud Computing
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Data Analytics
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  AI & Machine Learning
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">Specialized Services</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  VLSI Design & Services
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Cybersecurity
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Blockchain & Web3
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                  Game Development
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation Tips - Responsive */}
        <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-gray-800 dark:to-gray-700 p-6 sm:p-8 lg:p-10 rounded-lg mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400" />
            Navigation Tips
          </h3>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">For Clients</h4>
              <ol className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">1.</span>
                  <span>Explore our <Link to="/services" className="text-blue-600 dark:text-blue-400 hover:underline">Services</Link> to find what we offer</span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">2.</span>
                  <span>Check our <Link to="/portfolio" className="text-blue-600 dark:text-blue-400 hover:underline">Portfolio</Link> for case studies</span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">3.</span>
                  <span><Link to="/signup" className="text-blue-600 dark:text-blue-400 hover:underline">Sign up</Link> to access your dashboard</span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">4.</span>
                  <span>Join our <Link to="/forum" className="text-blue-600 dark:text-blue-400 hover:underline">community forum</Link></span>
                </li>
              </ol>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm sm:text-base">For Job Seekers</h4>
              <ol className="space-y-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">1.</span>
                  <span>Visit our <Link to="/careers" className="text-blue-600 dark:text-blue-400 hover:underline">Careers</Link> page</span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">2.</span>
                  <span>Learn about us on <Link to="/about" className="text-blue-600 dark:text-blue-400 hover:underline">About Us</Link></span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">3.</span>
                  <span>Browse available positions and apply</span>
                </li>
                <li className="flex gap-2 sm:gap-3">
                  <span className="font-bold text-green-600 dark:text-green-400 flex-shrink-0">4.</span>
                  <span>Contact us via <Link to="/contact" className="text-blue-600 dark:text-blue-400 hover:underline">Contact</Link> page</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Site Structure - Responsive */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 sm:p-6 lg:p-8 rounded-lg mb-12 sm:mb-16 overflow-hidden">
          <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2">
            <Folder className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
            Complete Site Structure
          </h3>

          <div className="relative">
            <div className="overflow-x-auto">
              <pre className="bg-gray-900 text-gray-100 p-4 sm:p-6 rounded-lg text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap sm:whitespace-pre">
{`nevxoaulio-ventures.com/
├── / (Home)
├── /about (About Us)
├── /services (All Services)
│   ├── /services/software-development
│   ├── /services/cloud-computing
│   ├── /services/cyber-security
│   ├── /services/vlsi-design
│   ├── /services/ai-ml-solutions
│   ├── /services/data-analytics
│   ├── /services/vlsi
│   ├── /services/blockchain
│   ├── /services/game-development
│   ├── /services/ai-automation
│   └── /services/startup-mvp
├── /portfolio (Portfolio)
├── /blog (Blog)
│   └── /blog/:id (Individual Blog Post)
├── /careers (Careers)
├── /forum (Forum & Chat)
├── /feedback (Customer Feedback)
│   └── /feedback/:id (Single Feedback)
├── /contact (Contact)
├── /faq (FAQ)
├── /login (Client Login)
├── /signup (Client Signup)
├── /client/dashboard (Client Dashboard)
├── /admin/login (Admin Login)
├── /admin/dashboard (Admin Dashboard)
├── /admin/analytics (Analytics Dashboard)
├── /admin/manage-news (Manage News/Blog)
├── /admin/manage-feedback (Manage Feedback)
├── /admin/chat (Admin Chat Support)
├── /privacy (Privacy Policy)
├── /terms (Terms of Service)
└── /sitemap (Sitemap)`}
              </pre>
            </div>
          </div>
        </div>

        {/* Search Optimization - Responsive */}
        <div className="bg-blue-50 dark:bg-gray-800 p-6 sm:p-8 lg:p-10 rounded-lg mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl font-bold mb-4">SEO & Search Optimization</h3>
          <p className="text-gray-700 dark:text-gray-300 mb-4 text-sm sm:text-base">
            This sitemap is designed to help search engines like Google discover and index all pages on our website. All pages are:
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
              Properly structured with semantic HTML
            </li>
            <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
              Optimized for mobile devices
            </li>
            <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
              Fast-loading and responsive
            </li>
            <li className="flex items-start gap-2 text-gray-700 dark:text-gray-300 text-sm sm:text-base">
              <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
              Linked with internal navigation
            </li>
          </ul>
        </div>

        {/* Contact Section - Responsive */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 sm:p-8 lg:p-10 rounded-lg mb-12 sm:mb-16 text-center">
          <h3 className="text-xl sm:text-2xl font-bold mb-3">Can't Find What You're Looking For?</h3>
          <p className="mb-4 sm:mb-6 text-sm sm:text-base opacity-90">
            Have a question or need assistance? We're here to help.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 font-bold py-2 sm:py-3 px-6 sm:px-8 rounded-lg hover:bg-gray-100 transition-colors duration-300 text-sm sm:text-base"
          >
            Contact Us
          </Link>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-6 sm:pt-8 mt-8 sm:mt-12 text-center">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
            © {new Date().getFullYear()} Nevxoaulio Ventures. All rights reserved.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 sm:gap-6 text-xs sm:text-sm">
            <Link to="/privacy" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              Terms of Service
            </Link>
            <Link to="/sitemap" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              Sitemap
            </Link>
            <Link to="/contact" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sitemap;